import { frameProgress, frameTimestampMicroseconds } from "../../core/animation/frameSchedule";
import type {
  EncodedVideoSample,
  VideoEncodingProgress,
  VideoEncodingResult,
  VideoEncodingSettings,
} from "../../core/export/videoEncoding";
import { AppError } from "../../shared/errors/AppError";

export type FrameProvider = (frameIndex: number, signal: AbortSignal) => Promise<CanvasImageSource>;

export type WebCodecsEncodeOptions = {
  settings: VideoEncodingSettings;
  frameProvider: FrameProvider;
  signal?: AbortSignal;
  onProgress?: (progress: VideoEncodingProgress) => void;
};

function copyChunk(chunk: EncodedVideoChunk, metadata?: EncodedVideoChunkMetadata): EncodedVideoSample {
  const data = new Uint8Array(chunk.byteLength);
  chunk.copyTo(data);
  return {
    type: chunk.type,
    timestamp: chunk.timestamp,
    duration: chunk.duration ?? null,
    data,
    decoderConfig: metadata?.decoderConfig,
  };
}

export async function isVideoEncoderConfigSupported(settings: VideoEncodingSettings) {
  if (!("VideoEncoder" in globalThis)) return false;
  const support = await VideoEncoder.isConfigSupported({
    codec: settings.codec,
    width: settings.width,
    height: settings.height,
    bitrate: settings.bitrate,
    framerate: settings.schedule.fps,
    hardwareAcceleration: settings.hardwareAcceleration,
    latencyMode: "quality",
  });
  return support.supported === true;
}

export async function encodeVideoFrames({
  settings,
  frameProvider,
  signal: externalSignal,
  onProgress,
}: WebCodecsEncodeOptions): Promise<VideoEncodingResult> {
  if (!("VideoEncoder" in globalThis) || !("VideoFrame" in globalThis)) {
    throw new AppError("EXPORT_FAILED", "WebCodecs video encoding is unavailable in this browser.");
  }

  if (!(await isVideoEncoderConfigSupported(settings))) {
    throw new AppError("EXPORT_FAILED", `The selected codec ${settings.codec} is not supported for this export.`);
  }

  const controller = new AbortController();
  const abort = () => controller.abort(externalSignal?.reason);
  externalSignal?.addEventListener("abort", abort, { once: true });
  if (externalSignal?.aborted) abort();

  const samples: EncodedVideoSample[] = [];
  let encoderError: DOMException | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, metadata) => samples.push(copyChunk(chunk, metadata)),
    error: (error) => {
      encoderError = error;
      controller.abort(error);
    },
  });

  encoder.configure({
    codec: settings.codec,
    width: settings.width,
    height: settings.height,
    bitrate: settings.bitrate,
    framerate: settings.schedule.fps,
    hardwareAcceleration: settings.hardwareAcceleration,
    latencyMode: "quality",
  });

  try {
    for (let frameIndex = 0; frameIndex < settings.schedule.frameCount; frameIndex += 1) {
      if (controller.signal.aborted) {
        throw controller.signal.reason ?? new DOMException("Video export cancelled.", "AbortError");
      }

      const source = await frameProvider(frameIndex, controller.signal);
      const timestamp = frameTimestampMicroseconds(settings.schedule, frameIndex);
      const duration = Math.round(settings.schedule.frameDurationSeconds * 1_000_000);
      const frame = new VideoFrame(source, {
        timestamp,
        duration,
        visibleRect: {
          x: 0,
          y: 0,
          width: settings.width,
          height: settings.height,
        },
        displayWidth: settings.width,
        displayHeight: settings.height,
      });

      try {
        encoder.encode(frame, { keyFrame: frameIndex === 0 || frameIndex % Math.max(1, Math.round(settings.schedule.fps * 2)) === 0 });
      } finally {
        frame.close();
      }

      const encodedFrames = frameIndex + 1;
      onProgress?.({
        encodedFrames,
        totalFrames: settings.schedule.frameCount,
        progress: frameProgress(settings.schedule, frameIndex),
      });

      if (encoder.encodeQueueSize > 6) await encoder.flush();
    }

    await encoder.flush();
    if (encoderError) throw encoderError;

    return {
      codec: settings.codec,
      width: settings.width,
      height: settings.height,
      fps: settings.schedule.fps,
      samples,
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new AppError("EXPORT_FAILED", "Video encoding failed.", error);
  } finally {
    externalSignal?.removeEventListener("abort", abort);
    if (encoder.state !== "closed") encoder.close();
  }
}
